import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  createDeepLinkingCopiedSuccessfullyEvent,
  createDeepLinkingSharingModalOpenedEvent,
} from '@/scopes/prices/domain/analytics'

const SheetContent = styled(Sheet.Content)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${space('primitives_space_24')} ${space('primitives_space_32')} !important;
`
const ShareInput = styled.div`
    display: flex;
    align-items: center;
    height: 44px;
    border-radius: ${tokPx('primitives_corner_radius_m')};
    border: 2px solid ${tok('primitives_color_neutral_400')};
    width: 100%;
    box-sizing: border-box;
    padding: 0 0 0 ${space('primitives_space_16')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    margin: ${space('primitives_space_12')} 0;
    text-align: center;
`
const ShareDescription = styled(Typography)`
    text-align: center;
`
const OpacityGradient = styled.div`
    position: absolute;
    right: 0;
    width: 20px;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 100%
    );
`
const CopyButton = styled(Button)<{ $copied?: boolean }>`
    width: 100%;
    ${({ $copied }) => $copied && css`
      background-color: ${tok('primitives_color_selected_500')};
      color: ${tok('primitives_color_neutral_0')};
      &:hover {
        background-color: ${tok('primitives_color_selected_500')};
        color: ${tok('primitives_color_neutral_0')};
      }
    `}
`
export function ShareButton() {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const currentUrl = window.location.href
  const utmParam = 'utm_source=sunweb&utm_medium=website&utm_campaign=deeplink'
  const copyUrl = `${currentUrl}&${utmParam}`
  const { sendEvent } = useAnalytics()
  const userAgent = navigator.userAgent || navigator.vendor
  const isAndroidDevice = /android/i.test(userAgent)

  const copiedSuccessfully = (result: boolean) => {
    sendEvent(createDeepLinkingCopiedSuccessfullyEvent(result))
    setCopied(result)
  }

  const sendOpenedShareModalEvent = () => {
    sendEvent(createDeepLinkingSharingModalOpenedEvent())
  }

  const handleShare = async () => {
    sendOpenedShareModalEvent()
    const shareData = {
      title: intl.formatMessage({ id: 'price_basket_share_native_window_title' }),
      url: copyUrl,
    }
    const canShare = navigator.canShare?.(shareData)
    if (canShare) {
      try {
        await navigator.share(shareData).then(() => {
          if (isAndroidDevice) {
            copiedSuccessfully(true)
          }
        })
      }
      catch {
        copiedSuccessfully(false)
      }
    }
    else {
      setOpen(true)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl)
      copiedSuccessfully(true)
    }
    catch {
      console.warn(`Could not copy ${copyUrl}`)
      copiedSuccessfully(false)
    }
  }

  const handleCloseModal = () => {
    setOpen(false)
    setCopied(false)
  }
  return (
    <>
      <Button variant="secondary" onClick={handleShare}>
        <Button.Icon glyph="SquareArrowUp" />
        {intl.formatMessage({ id: 'price_basket_share_button_text' })}
      </Button>
      <Sheet.Root open={open} onOpenChange={handleCloseModal}>
        <Sheet.Body side="right" bottomSideInMobile>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'price_basket_share_native_window_title' })}
          />
          <SheetContent>
            <Typography variant="body-l-bold-snug">{intl.formatMessage({ id: 'price_basket_share_sheet_content_title' })}</Typography>
            <ShareDescription variant="body-m-regular-snug">{intl.formatMessage({ id: 'price_basket_share_sheet_content_description' })}</ShareDescription>
            <ShareInput>
              <Typography variant="body-m-regular-snug">{currentUrl}</Typography>
              <OpacityGradient />
            </ShareInput>
            <CopyButton variant="secondary" $copied={copied} onClick={handleCopy}>
              {copied
                ? <Button.Icon glyph="CheckMarkSmall" />
                : <Button.Icon glyph="SquareOnSquare" />}
              {copied ? intl.formatMessage({ id: 'price_basket_share_sheet_copied' }) : intl.formatMessage({ id: 'price_basket_share_sheet_copy_link' })}
            </CopyButton>
          </SheetContent>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}
